import { useState, useMemo, useEffect } from "react";
import { ProductFull } from "../Cart/CartAction";
import { Vourcher, VoucherQuoteResponse } from "../../api/VourcherType";
import { createOrderV3 } from "api/Order";
import { removeMultipleItemsFromCart } from "../../api/Cart";
import { fetchGiftById } from "../../api/Gift";
import { toast } from "react-toastify";
import { clearVoucherBooking, matchesVoucherBooking, readVoucherBooking } from "../../utils/checkoutVoucherBooking";

const TEMP_SHIPPING_FEE = 0;

interface RightSidebarProps {
    selectedItems: ProductFull[];
    selectedVouchers: Vourcher[];
    selectedPointVoucherGiftIds?: number[];
    selectedPointProductGiftIds?: number[];
    quoteSnapshot?: VoucherQuoteResponse | null;
    fullname?: string;
    phone?: string;
    address?: string;
    paymentMethod?: string;
    note?: string;
    onRequireShippingInfo?: () => void;
}

interface GiftDisplayDetail {
    id: number;
    name: string;
    description: string;
    points: number;
    discount_value?: number;
    discount_type?: 'fixed' | 'percent' | 'ship' | 'gift' | string;
    image?: string;
}

interface QuoteBreakdownDisplayItem {
    code?: string;
    gift_id?: number;
    success: boolean;
    template_name?: string;
    discount_applied?: number;
    discount_value?: number | string;
    template_value?: number | string;
    applied_to?: 'order' | 'shipping';
    type?: 'fixed' | 'percent' | 'ship' | 'gift';
    message?: string;
}

export default function RightSidebar({
    selectedItems = [],
    selectedVouchers = [],
    selectedPointVoucherGiftIds = [],
    selectedPointProductGiftIds = [],
    quoteSnapshot = null,
    fullname = '',
    phone = '',
    address = '',
    paymentMethod = 'PAYMENT_METHOD_BANK',
    note = '',
    onRequireShippingInfo
}: RightSidebarProps) {
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [isOrderSuccessModalOpen, setIsOrderSuccessModalOpen] = useState(false);
    const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
    const [bookingIds, setBookingIds] = useState<number[]>([]);
    const [reservedCodes, setReservedCodes] = useState<string[]>([]);
    const [giftDetailsMap, setGiftDetailsMap] = useState<Record<number, GiftDisplayDetail>>({});

    async function handlePlaceOrder() {
        if (isPlacingOrder) {
            return;
        }

        if (!fullname || !phone || !address) {
            toast.warning("Vui lòng bổ sung thông tin giao hàng.", {
                position: "bottom-right",
            });
            onRequireShippingInfo?.();
            return;
        }

        const productList = selectedItems.map(item => ({
            id: item.id,
            quantity: item.qty,
        }));

        const selectedGiftIds = [...selectedPointVoucherGiftIds, ...selectedPointProductGiftIds]
            .map((giftId) => Number(giftId))
            .filter((giftId) => Number.isFinite(giftId) && giftId > 0);

        const redeemGiftPayload = selectedGiftIds
            .map((giftId) => ({
                gift_id: giftId,
                qty: 1,
            }));

        if (productList.length === 0) {
            toast.error("Vui lòng chọn sản phẩm trước khi đặt hàng.", {
                position: "bottom-right",
            });
            return;
        }

        const customerId = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user") || '{}').id : null;
        if (!customerId) {
            toast.error("Vui lòng đăng nhập để đặt hàng.", {
                position: "bottom-right",
            });
            return;
        }

        // Save purchased items to localStorage
        const savePurchaseHistory = () => {
            try {
                const stored = localStorage.getItem("purchased_history");
                const history = stored ? JSON.parse(stored) : [];
                const newPurchase = {
                    date: new Date().toISOString(),
                    items: selectedItems
                };
                // Add new purchase to the beginning
                history.unshift(newPurchase);
                // Limit history size if needed (e.g., last 50 orders)
                if (history.length > 50) history.splice(50);
                localStorage.setItem("purchased_history", JSON.stringify(history));
            } catch (error) {
                console.error("Failed to save purchase history", error);
            }
        };

        try {
            setIsPlacingOrder(true);
            const selectedCodes = selectedVouchers.map(v => v.code);

            const reserveItems = selectedItems.map(item => ({
                product_id: item.id,
                quantity: item.qty,
            }));

            const storedBooking = readVoucherBooking();

            if (selectedCodes.length > 0) {
                if (!matchesVoucherBooking(selectedCodes, reserveItems, storedBooking)) {
                    toast.error("Voucher chưa được giữ chỗ hợp lệ. Vui lòng quay lại giỏ hàng để áp dụng lại voucher.", {
                        position: "bottom-right",
                    });
                    setIsPlacingOrder(false);
                    return;
                }

                const nextBookingIds = storedBooking?.bookingIds || [];
                const nextReservedCodes = storedBooking?.reservedCodes || [];
                setBookingIds(nextBookingIds);
                setReservedCodes(nextReservedCodes);

                if (nextBookingIds.length === 0) {
                    toast.error("Voucher chưa được giữ chỗ. Vui lòng quay lại giỏ hàng để áp dụng lại voucher.", {
                        position: "bottom-right",
                    });
                    setIsPlacingOrder(false);
                    return;
                }

                const missingReservedCode = selectedCodes.find(code => !nextReservedCodes.includes(code));
                if (missingReservedCode) {
                    toast.error(`Voucher ${missingReservedCode} chưa giữ chỗ thành công.`, {
                        position: "bottom-right",
                    });
                    setIsPlacingOrder(false);
                    return;
                }
            }

            const response = await createOrderV3({
                list_products: JSON.stringify(productList),
                booking_ids: bookingIds,
                redeem_gifts: redeemGiftPayload.length > 0 ? JSON.stringify(redeemGiftPayload) : undefined,
                fullname: fullname,
                phone: phone,
                address: address,
                payment: paymentMethod === 'PAYMENT_METHOD_COD' ? 1 : 2,
                note: note,
            });

            const hasData = (res: unknown): res is { data?: { id?: number } } =>
                typeof res === "object" && res !== null && "data" in res;

            const hasError = (res: unknown): res is { error?: { error?: unknown; message?: string } } =>
                typeof res === "object" && res !== null && "error" in res;

            if (response.status) {
                savePurchaseHistory();
                clearVoucherBooking();

                const cartItemIds = selectedItems
                    .map(item => item.cart_item_id)
                    .filter((id): id is number => id !== undefined)
                    .map(String);

                if (cartItemIds.length > 0) {
                    await removeMultipleItemsFromCart(cartItemIds);
                }

                const orderId = hasData(response) ? (response.data?.id ?? null) : null;
                setCreatedOrderId(orderId);
                setIsOrderSuccessModalOpen(true);
                return;
            } else {
                let errorText = "Lỗi không xác định";

                if (hasError(response)) {
                    const raw = response.error?.error ?? response.error?.message ?? response.error;
                    errorText = typeof raw === "string" ? raw : JSON.stringify(raw ?? "Lỗi không xác định");
                }

                toast.error(`Đặt hàng thất bại: ${errorText}`, {
                    position: "bottom-right",
                });
            }


        } catch (error: any) {
            console.error("Error creating order:", error);
            toast.error(`Đặt hàng thất bại: ${error.message}`, {
                position: "bottom-right",
            });
        } finally {
            setIsPlacingOrder(false);
        }
    }

    useEffect(() => {
        const loadGiftDetails = async () => {
            const allIds = [...selectedPointVoucherGiftIds, ...selectedPointProductGiftIds]
                .map((giftId) => Number(giftId))
                .filter((giftId) => Number.isFinite(giftId) && giftId > 0);

            if (allIds.length === 0) {
                setGiftDetailsMap({});
                return;
            }

            const uniqueIds = Array.from(new Set(allIds));
            const entries = await Promise.all(
                uniqueIds.map(async (giftId) => {
                    const detailRes = await fetchGiftById(giftId);
                    if (!detailRes?.status || !detailRes.data) return null;

                    const detail = detailRes.data as any;
                    return [
                        giftId,
                        {
                            id: giftId,
                            name: detail.name || detail.title || detail.code || `Gift #${giftId}`,
                            description:
                                detail.describe ||
                                detail.description ||
                                detail.condition ||
                                detail.note ||
                                "Ưu đãi đổi điểm",
                            points: Number(detail.point || detail.point_cost || detail.point_required || detail.price || 0),
                            discount_value: Number(detail.discount_value || 0),
                            discount_type: detail.type,
                            image: detail.image || detail.avatar || detail.thumbnail,
                        } as GiftDisplayDetail,
                    ] as const;
                })
            );

            const nextMap: Record<number, GiftDisplayDetail> = {};
            entries.forEach((entry) => {
                if (!entry) return;
                nextMap[entry[0]] = entry[1];
            });

            setGiftDetailsMap(nextMap);
        };

        loadGiftDetails();
    }, [selectedPointVoucherGiftIds, selectedPointProductGiftIds]);

    useEffect(() => {
        const syncBookingFromStorage = () => {
            const selectedCodes = selectedVouchers.map(v => v.code).filter(Boolean);
            const reserveItems = selectedItems.map(item => ({
                product_id: item.id,
                quantity: item.qty,
            }));

            if (selectedCodes.length === 0 || reserveItems.length === 0) {
                setBookingIds([]);
                setReservedCodes([]);
                clearVoucherBooking();
                return;
            }

            const storedBooking = readVoucherBooking();
            if (matchesVoucherBooking(selectedCodes, reserveItems, storedBooking)) {
                setBookingIds(storedBooking?.bookingIds || []);
                setReservedCodes(storedBooking?.reservedCodes || []);
                return;
            }

            setBookingIds([]);
            setReservedCodes([]);
        };

        syncBookingFromStorage();
    }, [selectedVouchers, selectedItems]);

    // Calculate totals
    const { totalQuantity, totalPrice } = useMemo(() => {
        const totalQty = selectedItems.reduce((sum, item) => sum + item.qty, 0);
        const totalP = selectedItems.reduce((sum, item) => sum + (item.rawPrice * item.qty), 0);
        return {
            totalQuantity: totalQty,
            totalPrice: totalP,
        };
    }, [selectedItems]);

    const quoteData = quoteSnapshot?.success ? quoteSnapshot.data : undefined;
    const displayPricing = useMemo(() => {
        if (quoteData) {
            return {
                itemsAmount: quoteData.original_items_amount ?? totalPrice,
                shippingFee: quoteData.final_shipping_fee ?? quoteData.original_shipping_fee ?? TEMP_SHIPPING_FEE,
                discount: quoteData.total_discount ?? 0,
                finalAmount: quoteData.final_total_amount ?? (totalPrice + TEMP_SHIPPING_FEE),
            };
        }

        return {
            itemsAmount: totalPrice,
            shippingFee: TEMP_SHIPPING_FEE,
            discount: 0,
            finalAmount: totalPrice + TEMP_SHIPPING_FEE,
        };
    }, [quoteData, totalPrice]);

    const selectedProductGiftDetails = useMemo(
        () => selectedPointProductGiftIds.map((giftId) => giftDetailsMap[giftId]).filter(Boolean),
        [selectedPointProductGiftIds, giftDetailsMap]
    );

    const selectedVoucherGiftDetails = useMemo(
        () => selectedPointVoucherGiftIds.map((giftId) => giftDetailsMap[giftId]).filter(Boolean),
        [selectedPointVoucherGiftIds, giftDetailsMap]
    );

    const successfulBreakdownItems = useMemo(() => {
        const breakdown = (quoteSnapshot?.data?.breakdown || []) as QuoteBreakdownDisplayItem[];
        return breakdown.filter((item) => item.success);
    }, [quoteSnapshot]);

    const getBreakdownLabel = (item: QuoteBreakdownDisplayItem) => {
        if (item.code) {
            return `Mã ${item.code}`;
        }

        if (item.gift_id) {
            const giftName = giftDetailsMap[item.gift_id]?.name;
            if (item.type === 'gift') {
                return `Quà đổi điểm${giftName ? `: ${giftName}` : ` #${item.gift_id}`}`;
            }
            return `Ưu đãi đổi điểm${giftName ? `: ${giftName}` : ` #${item.gift_id}`}`;
        }

        return item.template_name || 'Ưu đãi';
    };

    const getBreakdownTypeText = (type?: QuoteBreakdownDisplayItem['type']) => {
        switch (type) {
            case 'fixed':
                return 'Giảm cố định';
            case 'percent':
                return 'Giảm theo %';
            case 'ship':
                return 'Giảm phí vận chuyển';
            case 'gift':
                return 'Quà tặng';
            default:
                return 'Ưu đãi';
        }
    };

    const formatBreakdownDiscountText = (item: QuoteBreakdownDisplayItem) => {
        const appliedDiscount = Number(item.discount_applied || 0);
        if (Number.isFinite(appliedDiscount) && appliedDiscount > 0) {
            return `-${appliedDiscount.toLocaleString('vi-VN')}đ`;
        }

        const discountValue = Number(item.discount_value || 0);
        if (Number.isFinite(discountValue) && discountValue > 0) {
            if (item.type === 'percent') {
                return `-${discountValue}%`;
            }
            return `-${discountValue.toLocaleString('vi-VN')}đ`;
        }

        return '-0đ';
    };

    const formatVoucherGiftValue = (gift: GiftDisplayDetail) => {
        const discountValue = Number(gift.discount_value || 0);
        if (Number.isFinite(discountValue) && discountValue > 0) {
            if (gift.discount_type === 'percent') {
                return `${discountValue}%`;
            }
            return `${discountValue.toLocaleString('vi-VN')}đ`;
        }

        return `${gift.points.toLocaleString('vi-VN')} điểm`;
    };

    return (
        <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-3 mobile-mt-24">
            <div className="styles_checkout_sticky__598hV">
                <div className="MuiPaper-root styles_root__UKeJN MuiPaper-elevation0 MuiPaper-rounded">

                    <div className="styles_d_flex__gMFmz">
                        <div className="styles_checkout_label_bold__KZdZj">Thông tin đơn hàng</div>

                        <div className="styles_checkout_content__5fBwM">
                            <button
                                className="MuiButtonBase-root MuiButton-root MuiButton-outlined sc-2362e412-0 sc-2362e412-2 iQEreR egmplk styles_btn__bDJ05"
                                type="button"
                                title="Sửa đơn hàng"
                                onClick={() => {
                                    window.location.href = "/cart";
                                }}
                            >
                                <span className="MuiButton-label">Sửa</span>
                            </button>
                        </div>
                    </div>

                    <div className="styles_d_flex__gMFmz">
                        <div className="styles_checkout_label__86Gzy">Tổng số lượng</div>
                        <div className="styles_checkout_content__5fBwM">{totalQuantity} sản phẩm</div>
                    </div>

                    <div className="styles_d_flex__gMFmz">
                        <div className="styles_checkout_label__86Gzy">Tổng tiền hàng</div>
                        <div className="styles_checkout_content__5fBwM">{displayPricing.itemsAmount.toLocaleString('vi-VN')}đ</div>
                    </div>

                    {quoteData?.point_earned != null && quoteData.point_earned > 0 && (
                        <div className="styles_d_flex__gMFmz">
                            <div className="styles_checkout_label__86Gzy">Điểm tích lũy</div>
                            <div className="styles_checkout_content__5fBwM" style={{ color: '#e88438' }}>+{quoteData.point_earned.toLocaleString('vi-VN')}</div>
                        </div>
                    )}

                    <hr className="MuiDivider-root styles_dividerCustom__BK2Vd" style={{ marginBottom: 16 }} />

                    {/* Discount */}
                    <div className="styles_boxDiscountSection__82B_y">
                        <div className="styles_d_flex__gMFmz" style={{ padding: 0 }}>
                            <div className="styles_checkout_label__86Gzy">Tổng ưu đãi</div>
                            <div className="styles_checkout_content__5fBwM">-{successfulBreakdownItems.length > 0 ? Number(successfulBreakdownItems[0].template_value).toLocaleString('vi-VN', {maximumFractionDigits: 0, minimumFractionDigits: 0}) : 0} {successfulBreakdownItems.length > 0 && successfulBreakdownItems[0].type == 'fixed' ? 'đ' : '%'}</div>
                        </div>

                        {/* Quote breakdown lines */}
                        {successfulBreakdownItems.length > 0
                            ? successfulBreakdownItems.map((item, idx) => {
                                console.log("Breakdown item:", item);
                                const discountText = formatBreakdownDiscountText(item);
                                const appliedToText = item.applied_to === 'shipping' ? 'Phí vận chuyển' : 'Đơn hàng';
                                const typeText = getBreakdownTypeText(item.type);
                                const template_value = Number(item.template_value).toLocaleString('vi-VN', {maximumFractionDigits: 0, minimumFractionDigits: 0});

                                return (
                                    <div key={`${item.code || item.gift_id || 'breakdown'}-${idx}`} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo">{getBreakdownLabel(item)}</div>
                                        <div style={{ marginTop: 2, fontSize: 12, color: "#6b7280" }}>
                                            {`${item.template_name || typeText} • ${template_value} đ`}
                                        </div>
                                    </div>
                                );
                            })
                            : selectedVouchers.map(v => {
                                return (
                                    <div key={v.id} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo">Mã {v.code}</div>
                                    </div>
                                );
                            })}

                    </div>

                    {selectedPointProductGiftIds.length > 0 && (
                        <div className="styles_boxDiscountSection__82B_y">
                            <div className="styles_d_flex__gMFmz" style={{ padding: 0 }}>
                                <div className="styles_checkout_label__86Gzy">Quà đổi điểm</div>
                                <div className="styles_checkout_content__5fBwM">{selectedPointProductGiftIds.length} mục</div>
                            </div>

                            {selectedProductGiftDetails.length > 0
                                ? selectedProductGiftDetails.map((gift, idx) => (
                                    <div key={`${gift.id}-${idx}`} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            {gift.image ? (
                                                <img src={gift.image} alt={gift.name} style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover" }} />
                                            ) : null}
                                            <span>{gift.name} • {formatVoucherGiftValue(gift)}</span>
                                        </div>
                                        <div style={{ marginLeft: gift.image ? 32 : 0, fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                            {gift.description}
                                        </div>
                                    </div>
                                ))
                                : selectedPointProductGiftIds.map((giftId, idx) => (
                                    <div key={`${giftId}-${idx}`} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo">Gift ID: {giftId}</div>
                                    </div>
                                ))}
                        </div>
                    )}

                    {selectedPointVoucherGiftIds.length > 0 && (
                        <div className="styles_boxDiscountSection__82B_y">
                            <div className="styles_d_flex__gMFmz" style={{ padding: 0 }}>
                                <div className="styles_checkout_label__86Gzy">Voucher đổi điểm</div>
                                <div className="styles_checkout_content__5fBwM">{selectedPointVoucherGiftIds.length} mục</div>
                            </div>

                            {selectedVoucherGiftDetails.length > 0
                                ? selectedVoucherGiftDetails.map((gift, idx) => (
                                    <div key={`${gift.id}-${idx}`} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            {gift.image ? (
                                                <img src={gift.image} alt={gift.name} style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover" }} />
                                            ) : null}
                                            <span>{gift.name} • {gift.points.toLocaleString('vi-VN')} điểm</span>
                                        </div>
                                        <div style={{ marginLeft: gift.image ? 32 : 0, fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                                            {gift.description}
                                        </div>
                                    </div>
                                ))
                                : selectedPointVoucherGiftIds.map((giftId, idx) => (
                                    <div key={`${giftId}-${idx}`} className="styles_boxSubDataSection__F1mot">
                                        <div className="styles_checkout_label_subdata__n7qjo">Gift ID: {giftId}</div>
                                    </div>
                                ))}
                        </div>
                    )}

                    <hr className="MuiDivider-root styles_dividerCustom__BK2Vd" />

                    {/* Total price */}
                    <div className="styles_d_flex__gMFmz styles_totalPrice__BFKN0">
                        <div className="styles_checkout_label_bold__KZdZj" style={{ fontSize: 16 }}>
                            Tổng tiền
                        </div>
                        <div className="styles_total__I_U2S">{displayPricing.finalAmount.toLocaleString('vi-VN')}đ</div>
                    </div>

                    <hr className="MuiDivider-root styles_dividerCustom__BK2Vd" />

                    {/* Agreement */}
                    <div>
                        <div className="styles_condition__1HFqu">
                            <label className="MuiFormControlLabel-root styles_formControlLabel__9jzcQ">
                                <span className="MuiButtonBase-root MuiIconButton-root jss1 MuiCheckbox-root styles_checkbox__j3W3G jss2 Mui-checked">
                                    <span className="MuiIconButton-label">
                                        <input
                                            className="jss4"
                                            name="checked"
                                            type="checkbox"
                                            defaultChecked
                                        />
                                        <svg className="MuiSvgIcon-root" viewBox="0 0 24 24">
                                            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                        </svg>
                                    </span>
                                </span>

                                <span className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1">
                                    <div className="styles_labelConfirm__SnOWJ" style={{ display: "inline-flex" }}>
                                        <div className="styles_agreement__Lj28J">Tôi đồng ý với&nbsp;</div>
                                        <a
                                            className="styles_mobile_rootBase__8z7PQ styles_check_agree_txt__jgV_U"
                                            href="/terms-and-condition"
                                            target="_blank"
                                        >
                                            <span style={{ color: "rgb(14,25,131)", textDecoration: "underline", fontSize: 14 }}>
                                                Điều khoản sử dụng
                                            </span>
                                        </a>
                                    </div>
                                </span>
                            </label>

                            <div className="styles_list_note__XUq_P">
                                <p>1. Buymed được phép hủy đơn của bạn nếu giá thị trường biến động lớn hơn 5% giá trị đơn hàng.</p>
                                <p>2. Số lượng sản phẩm khi giao có thể không đảm bảo đúng nhu cầu ban đầu tùy thuộc vào nhà cung cấp.</p>
                            </div>
                        </div>
                    </div>

                    {/* Order button */}
                    <div className="hidden-1199" style={{ display: "inline-flex", maxHeight: 40, padding: "0 16px", color: "#000", width: "100%" }}>
                        <div
                            id="delivery__info_checkout"
                            className="styles_wrapperGuide__w7nVA"
                            title="Vui lòng đồng ý với điều khoản sử dụng trước khi thanh toán"
                        >
                            <button
                                className="MuiButtonBase-root MuiButton-root MuiButton-text sc-2362e412-0 sc-2362e412-2 iQEreR egmplk payment_button styles_continue_btn__kSr4K"
                                type="button"
                                disabled={isPlacingOrder}
                                onClick={() => {
                                    handlePlaceOrder();
                                }}
                            >
                                <span className="MuiButton-label">
                                    {isPlacingOrder ? "Đang xử lý..." : (fullname && phone && address) ? "Đặt hàng" : "Thiếu thông tin"}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="styles_text_right__6Z7cA" />

                </div>
            </div>

            {/* mobile hien thi */}
            <div className="styles_sticky_checkout_bar_mobile__Jv0cq show-1199">
                <div className="styles_fwc_container__XUhdT">

                    <div className="styles_flex_price__Fq0ej">
                        <div style={{ textAlign: "right" }}>
                            <div>
                                <span className="MuiTypography-root styles_footerTotalPrice__P5gGa MuiTypography-body2">
                                    Tổng tiền: <span>{displayPricing.finalAmount.toLocaleString('vi-VN')}đ</span>
                                </span>
                            </div>

                            <div>
                                <span className="MuiTypography-root styles_footerTotalDiscount__mLQqT MuiTypography-body2">
                                    Tổng giảm giá: <span>{displayPricing.discount.toLocaleString('vi-VN')}đ</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            className="MuiButtonBase-root MuiButton-root styles_root_btn__BAofc MuiButton-text"
                            type="button"
                            disabled={isPlacingOrder}
                            onClick={() => {
                                handlePlaceOrder();
                            }}
                        >
                            <span className="MuiButton-label styles_label__n2CVj">
                                {isPlacingOrder ? "Đang xử lý..." : "Đặt hàng"}
                            </span>
                        </button>
                    </div>

                </div>
            </div>

            {isOrderSuccessModalOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        zIndex: 1300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: 560,
                            background: "#fff",
                            borderRadius: 8,
                            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.24)",
                            padding: "40px 24px 28px",
                            textAlign: "center",
                        }}
                    >
                        <div
                            aria-hidden="true"
                            style={{
                                margin: "0 auto 16px",
                                width: 84,
                                height: 84,
                                border: "4px solid #111",
                                borderRadius: 8,
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    top: -17,
                                    width: 38,
                                    height: 24,
                                    border: "4px solid #111",
                                    borderBottom: "none",
                                    borderTopLeftRadius: 18,
                                    borderTopRightRadius: 18,
                                    backgroundColor: "#fff",
                                }}
                            />
                            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 12.5L10.5 16L17 9.5" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>

                        <div style={{ fontSize: 30, lineHeight: "34px", fontWeight: 700, marginBottom: 8 }}>ĐẶT HÀNG THÀNH CÔNG!</div>
                        <div style={{ color: "#4b5563", marginBottom: 24 }}>
                            Đơn hàng của bạn đã được ghi nhận và đang chờ xử lý.
                        </div>

                        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOrderSuccessModalOpen(false);
                                    window.location.href = "/";
                                }}
                                style={{
                                    minWidth: 170,
                                    height: 40,
                                    border: "1px solid #111",
                                    background: "#fff",
                                    color: "#111",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    cursor: "pointer",
                                }}
                            >
                                QUAY LẠI MUA HÀNG
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setIsOrderSuccessModalOpen(false);
                                    window.location.href = createdOrderId ? `/my-order/${createdOrderId}` : "/my-order";
                                }}
                                style={{
                                    minWidth: 170,
                                    height: 40,
                                    border: "1px solid #111",
                                    background: "#111",
                                    color: "#fff",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    cursor: "pointer",
                                }}
                            >
                                XEM CHI TIẾT
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
}
